import { BoatFilterSidebar, type BoatFilters, countBoatAttributeFilters, EMPTY_BOAT_ATTRIBUTE_FILTERS } from '@/components/boat-filter-sidebar';
import { type BoatOfferCardProps } from '@/components/boat-offer-card';
import { BoatResultsGrid } from '@/components/boat-results-grid';
import { BOAT_SORT_OPTIONS, BoatResultsHeader, LISTING_SORT_OPTIONS, resolveSortOption, type SortOption } from '@/components/boat-results-header';
import { CreateAlertDialog } from '@/components/create-alert-dialog';
import { countListingAttributeFilters, EMPTY_LISTING_ATTRIBUTE_FILTERS, ListingFilterSidebar } from '@/components/listing-filter-sidebar';
import { ListingResultsGrid } from '@/components/listing-results-grid';
import { ListingStockAbout } from '@/components/listing-stock-about';
import { ListingStockHeader } from '@/components/listing-stock-header';
import { formatRadiusLabel, locationSupportsRadius, resolveNextRadius } from '@/components/location-filter-field';
import { SearchAutocomplete } from '@/components/search-autocomplete';
import { SearchCriteriaChips } from '@/components/search/search-criteria-chips';
import { SmartSearchForm } from '@/components/search/smart-search-form';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '@/components/ui/drawer';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Pagination } from '@/components/ui/pagination';
import FrontendLayout from '@/layouts/frontend-layout';
import { apiClient } from '@/lib/apiClient';
import { buildClassifiedPath } from '@/lib/classified-url';
import { getPublicPriceLabel } from '@/lib/currency';
import { toJsonLd } from '@/lib/json-ld';
import { getMediaUrl } from '@/lib/media';
import {
    buildAlertCriteriaFromSearch,
    buildCreateAlertReturnUrl,
    buildSearchAlertCriteria,
    clearCreateAlertDraft,
    clearCreateAlertQueryParam,
    CREATE_ALERT_QUERY_PARAM,
    readCreateAlertDraft,
    saveCreateAlertDraft,
    type CreateAlertDraft,
} from '@/lib/search-alerts';
import { DEFAULT_SMART_SEARCH_SETTINGS, SMART_MISS_PARAM, SMART_QUERY_PARAM, trackSearchEvent } from '@/lib/smart-search';
import { cn } from '@/lib/utils';
import { login, search } from '@/routes';
import { show as showListing } from '@/routes/listings';
import { count as searchCountRoute } from '@/routes/search';
import { type SearchAlertSource } from '@/types/search-alert';
import { type SearchCriteriaData, type SearchCriteriaLabel, type ZeroResultSuggestion } from '@/types/search-criteria';
import {
    type CategoryBreadcrumbItem,
    type CategoryFaqItem,
    type CategorySeoData,
    type Classified,
    type FilterOptions,
    type Listing,
    type ListingCardProps,
    type ListingFilters,
    type ListingStockProfile,
    type LocationFilterData,
    type MediaItem,
    type PaginatedData,
    type SearchFilters,
    type SelectedLocation,
    type SharedData,
} from '@/types';
import { Head, router, usePage } from '@inertiajs/react';
import DOMPurify from 'isomorphic-dompurify';
import { ArrowUpDown, BellRing, ChevronDown, ChevronUp, SlidersHorizontal, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface Banner {
    id: number;
    title: string;
    link_url: string | null;
    section: string;
    is_active: boolean;
    description: string | null;
    image_alt: string | null;
    desktop_image: MediaItem | null;
    mobile_image: MediaItem | null;
    views_count: number;
    clicks_count: number;
}

interface Props {
    results: PaginatedData<Classified | Listing>;
    filterOptions: FilterOptions;
    filters: SearchFilters;
    type?: 'embarcacoes' | 'empresas';
    classifiedBanners?: Banner[];
    categorySeo?: CategorySeoData | null;
    listingProfile?: ListingStockProfile | null;
    locationFilter?: LocationFilterData | null;
    /** CN-200: criteria package of the current search (labels with removal URLs); absent on pages that do not build it. */
    searchCriteria?: SearchCriteriaData | null;
    /** CN-200: phrase of the smart search that produced this page (null once the visitor changed a filter by hand). */
    smartSearchPhrase?: string | null;
    /** CN-200: the AI could not turn the phrase into filters; the results are the text search. */
    smartSearchMissed?: boolean;
    /** CN-200: with no results, the label whose removal frees the most results. */
    zeroResultSuggestion?: ZeroResultSuggestion | null;
}

type ResultsType = 'embarcacoes' | 'empresas';
type InertiaPayload = Record<string, string | number | boolean | null | undefined | string[] | number[]>;

interface ResultsSearchParams extends Omit<SearchFilters, 'type'> {
    type?: ResultsType;
    ai_query?: string;
    ai_miss?: number;
}

const DEFAULT_YEAR_MIN = 1990;
const DEFAULT_PRICE_MAX = 1000000;
const DEFAULT_SAILING_MAX = 10000;
const FALLBACK_RESULT_IMAGE = '/boat-1.jpg';
const ALLOWED_CONTENT_TAGS = ['p', 'br', 'ul', 'ol', 'li', 'strong', 'b', 'em', 'i', 'u', 'blockquote', 'a', 'h2', 'h3', 'h4'];
const ALLOWED_CONTENT_ATTRIBUTES = ['href', 'target', 'rel'];

const normalizeCategory = (category: SearchFilters['category']) => {
    if (!category) {
        return [];
    }

    if (Array.isArray(category)) {
        return category.map(String);
    }

    return [String(category)];
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

const formatLocation = (cityName?: string | null, stateName?: string | null) => {
    if (cityName && stateName) {
        return `${cityName}, ${stateName}`;
    }

    return cityName || stateName || '';
};

const normalizeText = (value?: string | null) => {
    if (!value) {
        return '';
    }

    return value.trim();
};

const stripHtml = (value: string) => {
    return value
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ')
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};

const sanitizeHtmlContent = (value: string) => {
    return DOMPurify.sanitize(value, {
        ALLOWED_TAGS: ALLOWED_CONTENT_TAGS,
        ALLOWED_ATTR: ALLOWED_CONTENT_ATTRIBUTES,
        FORBID_TAGS: ['script', 'style'],
        ALLOW_DATA_ATTR: false,
    }).trim();
};

const truncateText = (value: string, maxLength: number) => {
    if (value.length <= maxLength) {
        return value;
    }

    return `${value.slice(0, Math.max(maxLength - 1, 1)).trimEnd()}…`;
};

// Parâmetros que não mudam o conteúdo da lista (rastreamento/favorito pós-login) e não contam como filtro no canonical.
const CANONICAL_IGNORED_PARAMS = new Set(['gclid', 'fbclid', 'auto_favorite', 'favorite_id', 'favorite_listing_id', CREATE_ALERT_QUERY_PARAM]);

/**
 * Retorna o número da página quando a URL tem só paginação (?page=N, N >= 2), para o canonical
 * apontar para a própria página. Com qualquer filtro/ordenação, ?page=1 ou página além da última
 * (vazia, soft-404) retorna null e o canonical fica na URL base.
 */
const resolveSelfCanonicalPage = (
    pageUrl: string,
    pagination: Pick<PaginatedData<unknown>, 'current_page' | 'last_page' | 'data'> | undefined,
): number | null => {
    const currentPage = pagination?.current_page;

    if (!currentPage || currentPage < 2 || currentPage > (pagination?.last_page ?? 0) || (pagination?.data?.length ?? 0) === 0) {
        return null;
    }

    const queryIndex = pageUrl.indexOf('?');
    const params = new URLSearchParams(queryIndex >= 0 ? pageUrl.slice(queryIndex + 1) : '');
    const contentKeys: string[] = [];

    params.forEach((_value, key) => {
        if (!CANONICAL_IGNORED_PARAMS.has(key) && !key.startsWith('utm_')) {
            contentKeys.push(key);
        }
    });

    if (contentKeys.length !== 1 || contentKeys[0] !== 'page' || Number(params.get('page')) !== currentPage) {
        return null;
    }

    return currentPage;
};

// Marca no fim do seo_title (" | Compre Náutica", " - CompreNautica"...), para o sufixo de página entrar antes dela.
const TITLE_BRAND_SUFFIX_REGEX = /\s+[|–—-]\s+Compre\s?N[áa]utica\s*$/i;

/**
 * Acrescenta " – Página N" ao title: antes da marca quando o título termina com ela
 * ("Lanchas à venda | Compre Náutica" -> "Lanchas à venda – Página 2 | Compre Náutica"), senão no fim.
 */
const appendPageToTitle = (title: string, page: number) => {
    const suffix = ` – Página ${page}`;
    const brandMatch = title.match(TITLE_BRAND_SUFFIX_REGEX);

    if (!brandMatch || brandMatch.index === undefined || brandMatch.index === 0) {
        return `${title}${suffix}`;
    }

    return `${title.slice(0, brandMatch.index)}${suffix}${title.slice(brandMatch.index)}`;
};

const toAbsoluteUrl = (appUrl: string, path: string) => {
    try {
        if (/^https?:\/\//i.test(path)) {
            return path;
        }

        const base = appUrl?.trim() ? (appUrl.endsWith('/') ? appUrl : `${appUrl}/`) : window.location.origin;

        return new URL(path, base).toString();
    } catch {
        return path;
    }
};

const normalizeStringArray = (value: unknown): string[] => {
    if (!value) {
        return [];
    }

    if (Array.isArray(value)) {
        return value.map((item) => (typeof item === 'string' ? item.trim() : '')).filter((item) => item.length > 0);
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return [];
        }

        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed.map((item) => (typeof item === 'string' ? item.trim() : '')).filter((item) => item.length > 0);
            }
        } catch {
            return trimmed
                .split(/\r\n|\r|\n/)
                .map((item) => item.trim())
                .filter((item) => item.length > 0);
        }
    }

    return [];
};

const normalizeFaqItems = (value: unknown): CategoryFaqItem[] => {
    if (!value) {
        return [];
    }

    const rawItems = Array.isArray(value)
        ? value
        : typeof value === 'string'
          ? (() => {
                try {
                    const parsed = JSON.parse(value);
                    return Array.isArray(parsed) ? parsed : [];
                } catch {
                    return [];
                }
            })()
          : [];

    return rawItems
        .map((item) => {
            if (!item || typeof item !== 'object') {
                return null;
            }

            const entry = item as Record<string, unknown>;
            const questionSource = entry.question ?? entry.pergunta ?? entry.name;
            const answerSource = entry.answer ?? entry.resposta ?? entry.text;
            const question = typeof questionSource === 'string' ? questionSource.trim() : '';
            const answer = typeof answerSource === 'string' ? answerSource.trim() : '';

            if (!question || !answer) {
                return null;
            }

            return {
                question,
                answer,
            };
        })
        .filter((item): item is CategoryFaqItem => Boolean(item));
};

const normalizeBreadcrumbs = (value?: CategoryBreadcrumbItem[] | null): CategoryBreadcrumbItem[] => {
    if (!Array.isArray(value)) {
        return [];
    }
    return value
        .filter((item): item is CategoryBreadcrumbItem => Boolean(item?.name && item?.path))
        .map((item) => ({
            name: item.name.trim(),
            path: item.path,
        }))
        .filter((item) => item.name.length > 0);
};

const buildInitialBoatFilters = (
    initialFilters: SearchFilters,
    filterOptions: FilterOptions,
    locationFilter?: LocationFilterData | null,
): BoatFilters => {
    const yearMin = filterOptions?.yearRange?.min ?? DEFAULT_YEAR_MIN;
    const yearMax = filterOptions?.yearRange?.max ?? new Date().getFullYear();
    const priceMin = filterOptions?.priceRange?.min ?? 0;
    const priceMax = filterOptions?.priceRange?.max ?? DEFAULT_PRICE_MAX;
    const initialYearMin = clamp(initialFilters.year_min ?? yearMin, yearMin, yearMax);
    const initialYearMax = clamp(initialFilters.year_max ?? yearMax, yearMin, yearMax);
    const initialPriceMin = clamp(initialFilters.price_min ?? priceMin, priceMin, priceMax);
    const initialPriceMax = clamp(initialFilters.price_max ?? priceMax, priceMin, priceMax);
    const initialHoursMin = clamp(initialFilters.sailing_time_min ?? 0, 0, DEFAULT_SAILING_MAX);
    const initialHoursMax = clamp(initialFilters.sailing_time_max ?? DEFAULT_SAILING_MAX, 0, DEFAULT_SAILING_MAX);

    const positive = (value: unknown): number | null => {
        const parsed = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;

        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    };
    const flag = (value: unknown): boolean => value === 1 || value === true || value === '1' || value === 'true';
    const text = (value: unknown): string | null => (typeof value === 'string' && value.trim() !== '' ? value.trim().slice(0, 60) : null);

    return {
        category: normalizeCategory(initialFilters.category),
        condition: initialFilters.condition ?? 'all',
        location: locationFilter?.selected ?? null,
        radius: locationFilter?.radius ?? null,
        yearRange: [Math.min(initialYearMin, initialYearMax), Math.max(initialYearMin, initialYearMax)],
        priceRange: [Math.min(initialPriceMin, initialPriceMax), Math.max(initialPriceMin, initialPriceMax)],
        hours: [Math.min(initialHoursMin, initialHoursMax), Math.max(initialHoursMin, initialHoursMax)],
        ...EMPTY_BOAT_ATTRIBUTE_FILTERS,
        sizeMin: positive(initialFilters.size_min),
        sizeMax: positive(initialFilters.size_max),
        fuelType: text(initialFilters.fuel_type),
        engineBrand: text(initialFilters.engine_brand),
        cabin: flag(initialFilters.cabin),
        bathroom: flag(initialFilters.bathroom),
        generator: flag(initialFilters.generator),
        passengersMin: positive(initialFilters.passengers_min) ? Math.round(Number(initialFilters.passengers_min)) : null,
    };
};

const buildInitialListingFilters = (initialFilters: SearchFilters, locationFilter?: LocationFilterData | null): ListingFilters => {
    const flag = (value: unknown): boolean => value === 1 || value === true || value === '1' || value === 'true';
    const rating = Number(initialFilters.rating_min);

    return {
        category: normalizeCategory(initialFilters.category),
        location: locationFilter?.selected ?? null,
        radius: locationFilter?.radius ?? null,
        ...EMPTY_LISTING_ATTRIBUTE_FILTERS,
        service: typeof initialFilters.service === 'string' ? initialFilters.service.trim().slice(0, 60) : '',
        openSaturday: flag(initialFilters.open_saturday),
        openSunday: flag(initialFilters.open_sunday),
        openNow: flag(initialFilters.open_now),
        ratingMin: Number.isFinite(rating) && rating >= 1 && rating <= 5 ? rating : null,
        verified: flag(initialFilters.verified),
    };
};

// Identifica a localização vinda do servidor; muda quando a navegação troca de página ou de localização (ex.: /search → página geo).
const buildLocationPropsKey = (friendlyUrlPath: string | undefined, locationFilter?: LocationFilterData | null) =>
    JSON.stringify([
        friendlyUrlPath ?? null,
        locationFilter?.selected?.type ?? null,
        locationFilter?.selected?.id ?? null,
        locationFilter?.radius ?? null,
    ]);

const hasHttpStatus = (error: unknown, status: number) => {
    if (typeof error !== 'object' || error === null || !('response' in error)) {
        return false;
    }

    const response = (error as { response?: { status?: number } }).response;
    return response?.status === status;
};

const toBoatCards = (classifieds: Classified[], favoriteStates: Record<number, boolean>): BoatOfferCardProps[] => {
    return classifieds.map((classified) => ({
        id: classified.id,
        title: classified.title,
        href: buildClassifiedPath(classified.slug),
        location: formatLocation(classified.city?.name, classified.state?.name),
        price: getPublicPriceLabel({
            price: classified.price,
            priceOnRequest: classified.price_on_request,
        }),
        year: classified.year?.toString() || '--',
        engine_quantity: classified.engine_quantity ?? 0,
        engine_model: classified.engine_model,
        condition: classified.condition === 'new' ? 'Novo' : 'Usado',
        imageUrl: getMediaUrl(classified.main_image, 'card', getMediaUrl(classified.gallery?.[0], 'card', FALLBACK_RESULT_IMAGE)),
        isFavorited: favoriteStates[classified.id] ?? classified.is_favorited ?? false,
        isHighlighted: classified.is_highlighted,
        isCotaNautica: classified.is_cota_nautica,
    }));
};

const toListingCards = (listings: Listing[], favoriteStates: Record<number, boolean>): ListingCardProps[] => {
    return listings.map((listing) => {
        const category = listing.categories?.[0];
        const averageRating = (listing as Listing & { average_rating?: number }).average_rating ?? 0;
        const reviewCount = (listing as Listing & { review_count?: number }).review_count ?? 0;

        return {
            id: listing.id,
            title: listing.title,
            slug: listing.slug,
            href: showListing({ path: listing.slug }).url,
            category: category?.name || '',
            categoryFriendlyUrlPath: category?.friendly_url_path,
            location: formatLocation(listing.city?.name, listing.state?.name),
            averageRating,
            reviewCount,
            summary: listing.summary,
            imageUrl: getMediaUrl(listing.logo, 'logo', getMediaUrl(listing.gallery?.[0], 'card')),
            isFavorited: favoriteStates[listing.id] ?? listing.is_favorited ?? false,
            isVerified: Boolean(listing.is_verified),
            isFeatured: Boolean(listing.is_highlighted),
            services: Array.isArray(listing.services) ? listing.services : [],
            opensWeekend: Boolean((listing as Listing & { opens_weekend?: boolean }).opens_weekend),
            uniformHeight: true,
        };
    });
};

export default function Results({
    results,
    filterOptions,
    filters: initialFilters,
    type = 'embarcacoes',
    classifiedBanners = [],
    categorySeo = null,
    listingProfile = null,
    locationFilter = null,
    searchCriteria = null,
    smartSearchPhrase = null,
    smartSearchMissed = false,
    zeroResultSuggestion = null,
}: Props) {
    const { props: sharedProps, url: currentPageUrl } = usePage<SharedData>();
    const { auth, appUrl, smartSearch: smartSearchSettings = DEFAULT_SMART_SEARCH_SETTINGS } = sharedProps;
    const pageType: ResultsType = type === 'empresas' ? 'empresas' : 'embarcacoes';
    const sortOptions = pageType === 'empresas' ? LISTING_SORT_OPTIONS : BOAT_SORT_OPTIONS;

    const [favoriteStates, setFavoriteStates] = useState<Record<number, boolean>>({});
    const [filters, setFilters] = useState<BoatFilters>(() => buildInitialBoatFilters(initialFilters, filterOptions, locationFilter));
    const [listingFilters, setListingFilters] = useState<ListingFilters>(() => buildInitialListingFilters(initialFilters, locationFilter));
    const [sortBy, setSortBy] = useState<SortOption>(() => resolveSortOption(initialFilters.sort, sortOptions));
    // Modal "Criar alerta" (CN-192): aberto pela sidebar ou pelo estado sem resultados; após o login reabre com o rascunho.
    const [isCreateAlertOpen, setIsCreateAlertOpen] = useState(false);
    const [createAlertSource, setCreateAlertSource] = useState<SearchAlertSource>('busca');
    const [createAlertDraft, setCreateAlertDraft] = useState<CreateAlertDraft | null>(null);
    // CN-200: filters drawer of the phone (?filters=1 opens it, e.g. "Prefiro escolher nos filtros" on the home).
    const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);
    // CN-200: once the visitor changes a filter by hand, "Entendi assim:" becomes "Sua busca:" and the phrase leaves the URL.
    const [hasManualChange, setHasManualChange] = useState(false);
    const [drawerCount, setDrawerCount] = useState<{ status: 'idle' | 'loading' | 'ready' | 'error'; count: number | null }>({ status: 'idle', count: null });
    const drawerCountRequestRef = useRef(0);
    const [isSeoLongContentOpen, setIsSeoLongContentOpen] = useState(false);
    const [isIntroExpanded, setIsIntroExpanded] = useState(false);
    // Começa true para o "Ler mais" já sair visível no HTML do servidor; o efeito abaixo deixa o botão invisível se o texto couber em 2 linhas.
    const [isIntroClamped, setIsIntroClamped] = useState(true);
    // Localização escolhida no campo de busca: o texto digitado era o nome do lugar, então a busca por texto sai da URL.
    const [searchTermOverride, setSearchTermOverride] = useState<string | null>(null);
    const [openFaqIndexes, setOpenFaqIndexes] = useState<Set<number>>(new Set());

    const boatSidebarClass = 'lg:h-full';
    const listingSidebarClass = 'lg:h-full lg:overflow-y-auto lg:overscroll-contain';

    const timeoutRef = useRef<ReturnType<typeof window.setTimeout> | undefined>(undefined);
    const isInitialMount = useRef(true);
    const lastSearchSignatureRef = useRef('');
    const handledAutoFavoriteRef = useRef<string | null>(null);
    const handledCreateAlertReturnRef = useRef(false);
    const introTextRef = useRef<HTMLDivElement>(null);
    const previousPageTypeRef = useRef<ResultsType>(pageType);
    const prevInitialCategoryKeyRef = useRef<string>(JSON.stringify(normalizeCategory(initialFilters.category).sort()));
    const locationPropsKey = buildLocationPropsKey(categorySeo?.friendly_url_path, locationFilter);
    const prevLocationPropsKeyRef = useRef<string>(locationPropsKey);

    const yearMin = filterOptions?.yearRange?.min ?? DEFAULT_YEAR_MIN;
    const yearMax = filterOptions?.yearRange?.max ?? new Date().getFullYear();
    const priceMin = filterOptions?.priceRange?.min ?? 0;
    const priceMax = filterOptions?.priceRange?.max ?? DEFAULT_PRICE_MAX;

    const categoryBreadcrumbs = useMemo(() => normalizeBreadcrumbs(categorySeo?.breadcrumbs), [categorySeo?.breadcrumbs]);
    const isCategorySearchPage = Boolean(categorySeo?.friendly_url_path);
    const categoryHeadingTitle = normalizeText(categorySeo?.seo_h1) || normalizeText(categorySeo?.name);
    const categorySchemaName = normalizeText(categorySeo?.seo_h1) || categoryHeadingTitle;
    const categoryIntroText = normalizeText(categorySeo?.intro_text);
    // Páginas 2+ só com paginação: canonical próprio e " – Página N" no title/description, para não duplicar no Search Console.
    const selfCanonicalPage = isCategorySearchPage ? resolveSelfCanonicalPage(currentPageUrl, results) : null;
    const pageSuffix = selfCanonicalPage ? ` – Página ${selfCanonicalPage}` : '';
    const baseMetaDescription = normalizeText(categorySeo?.seo_description) || stripHtml(categoryIntroText);
    const categoryMetaDescription = baseMetaDescription ? `${truncateText(baseMetaDescription, 160 - pageSuffix.length)}${pageSuffix}` : '';
    const canonicalUrl = useMemo(() => {
        if (!isCategorySearchPage) {
            return '';
        }

        const baseUrl = toAbsoluteUrl(appUrl, categorySeo?.friendly_url_path ?? '/');

        if (!selfCanonicalPage) {
            return baseUrl;
        }

        try {
            const url = new URL(baseUrl);
            url.searchParams.set('page', String(selfCanonicalPage));
            return url.toString();
        } catch {
            return baseUrl;
        }
    }, [appUrl, categorySeo?.friendly_url_path, isCategorySearchPage, selfCanonicalPage]);
    const basePageTitle = isCategorySearchPage
        ? normalizeText(categorySeo?.seo_title)
            ? `${normalizeText(categorySeo?.seo_title)}`
            : `${categoryHeadingTitle || 'Categoria'} à venda`
        : 'Buscar';
    const pageTitle = selfCanonicalPage ? appendPageToTitle(basePageTitle, selfCanonicalPage) : basePageTitle;
    // Estoque da empresa não tem SEO H2 do admin: o H2 acima dos cards deixa claro quem anuncia. Sem contagem (regra de negócio).
    const resultsHeading =
        categorySeo?.page_kind === 'listing_stock' && normalizeText(categorySeo?.name)
            ? `Confira as embarcações anunciadas por ${normalizeText(categorySeo?.name)}`
            : undefined;
    const postListingTitle = normalizeText(categorySeo?.seo_h2) || `Mais sobre ${categoryHeadingTitle || 'esta categoria'}`;
    const resumoRapido = normalizeText(categorySeo?.resumo_rapido);
    const longDescription = normalizeText(categorySeo?.long_description);
    const pontosDestaque = useMemo(() => normalizeStringArray(categorySeo?.pontos_destaque), [categorySeo?.pontos_destaque]);
    const relatedTags = useMemo(() => normalizeStringArray(categorySeo?.related_tags), [categorySeo?.related_tags]);
    const synonyms = useMemo(() => normalizeStringArray(categorySeo?.synonyms), [categorySeo?.synonyms]);
    const faqItems = useMemo(() => normalizeFaqItems(categorySeo?.faq_json), [categorySeo?.faq_json]);
    const categorySocialImage = getMediaUrl(categorySeo?.logo, 'social', getMediaUrl(categorySeo?.icone, 'social'));

    const categoryById = useMemo(() => {
        return new Map(filterOptions.categories.map((category) => [category.id, category]));
    }, [filterOptions.categories]);

    const buildCategoryPath = useCallback(
        (categoryId: number): string | undefined => {
            const prefix = pageType === 'empresas' ? '/empresas' : '';
            let current = categoryById.get(categoryId);

            if (!current) {
                if (categorySeo?.id === categoryId && categorySeo?.friendly_url_path) {
                    return categorySeo.friendly_url_path;
                }
                return undefined;
            }

            const slugs: string[] = [];

            while (current) {
                if (current.slug) slugs.unshift(current.slug);

                if (current.parent_id == null) {
                    break;
                }

                const parent = categoryById.get(current.parent_id);

                if (!parent) {
                    if (categorySeo?.id === current.parent_id && categorySeo?.friendly_url_path) {
                        const [basePath] = categorySeo.friendly_url_path.split('?');
                        return `${basePath}/${slugs.join('/')}`;
                    }
                    break;
                }

                current = parent;
            }

            return slugs.length > 0 ? `${prefix}/${slugs.join('/')}` : undefined;
        },
        [categoryById, categorySeo?.id, categorySeo?.friendly_url_path, pageType],
    );

    // Valor de ?category= por categoria: "fabricante.modelo" para modelos (filter_key vindo do backend),
    // senão o slug. O slug sozinho é ambíguo: um modelo "26" seria lido como o ID 26.
    const categoryFilterKeyById = useMemo(() => {
        const lookup = new Map<number, string>();

        for (const category of filterOptions.categories) {
            const filterKey = category.filter_key ?? category.slug;

            if (filterKey) {
                lookup.set(category.id, filterKey);
            }
        }

        return lookup;
    }, [filterOptions.categories]);

    const resolveCategoryFilterKey = useCallback(
        (categoryId: number): string | undefined => {
            if (categorySeo?.id === categoryId) {
                const filterKey = categorySeo.filter_key ?? categorySeo.slug;

                if (filterKey) {
                    return filterKey;
                }
            }

            return categoryFilterKeyById.get(categoryId);
        },
        [categoryFilterKeyById, categorySeo?.id, categorySeo?.filter_key, categorySeo?.slug],
    );

    const activeCategoryIds = useMemo(() => {
        const activeFilters = pageType === 'empresas' ? listingFilters : filters;
        return activeFilters.category.map(Number).filter((id) => Number.isFinite(id) && id > 0);
    }, [pageType, listingFilters.category, filters.category]);

    const isGeoRoute = Boolean(categorySeo?.is_geo_route);
    // Estoque de uma empresa (/empresas/{slug}/embarcacoes): todo filtro permanece nessa URL.
    const isListingStockPage = categorySeo?.page_kind === 'listing_stock';

    const activeLocation = pageType === 'empresas' ? listingFilters.location : filters.location;
    const activeRadius = pageType === 'empresas' ? listingFilters.radius : filters.radius;

    // A página geo só se mantém enquanto a categoria e a localização forem as da página (cidade: só com "Somente na cidade").
    const staysOnGeoRoute = useMemo(() => {
        const geoCategoryId = categorySeo?.geo_category_id;
        const geoLocation = categorySeo?.geo_location;

        if (!isGeoRoute || geoCategoryId == null || !geoLocation || !activeLocation) {
            return false;
        }

        return (
            activeCategoryIds.length === 1 &&
            activeCategoryIds[0] === geoCategoryId &&
            activeLocation.type === geoLocation.type &&
            activeLocation.id === geoLocation.id &&
            !activeRadius
        );
    }, [activeCategoryIds, activeLocation, activeRadius, categorySeo?.geo_category_id, categorySeo?.geo_location, isGeoRoute]);

    const resultsRouteUrl = useMemo(() => {
        if ((staysOnGeoRoute || isListingStockPage) && categorySeo?.friendly_url_path) {
            return categorySeo.friendly_url_path;
        }

        if (activeCategoryIds.length === 0) {
            if (!isCategorySearchPage) {
                return search().url;
            }
            // Estava numa página de categoria e a seleção foi zerada.
            return pageType === 'empresas' ? '/empresas' : '/embarcacoes';
        }

        if (activeCategoryIds.length === 1) {
            // Categoria + localização vai para o /search, que redireciona para a página geo quando a combinação tem uma.
            if (activeLocation) {
                return search().url;
            }

            const path = buildCategoryPath(activeCategoryIds[0]);
            return path ?? search().url;
        }

        return search().url;
    }, [
        activeCategoryIds,
        activeLocation,
        staysOnGeoRoute,
        isListingStockPage,
        categorySeo?.friendly_url_path,
        isCategorySearchPage,
        pageType,
        buildCategoryPath,
    ]);

    useEffect(() => {
        if (previousPageTypeRef.current === pageType) {
            return;
        }

        previousPageTypeRef.current = pageType;
        prevLocationPropsKeyRef.current = locationPropsKey;
        setFilters(buildInitialBoatFilters(initialFilters, filterOptions, locationFilter));
        setListingFilters(buildInitialListingFilters(initialFilters, locationFilter));
        setSortBy(resolveSortOption(initialFilters.sort, sortOptions));
        isInitialMount.current = true;
        lastSearchSignatureRef.current = '';
    }, [filterOptions, initialFilters, locationFilter, locationPropsKey, pageType, sortOptions]);

    // When navigating between CategoryRoute pages (e.g. /lanchas → /lanchas/nx-boats),
    // Inertia preserves filter state but initialFilters.category reflects the new page.
    // Reset filters.category to match so stale parent IDs don't trigger a reverse navigation.
    useEffect(() => {
        if (!isCategorySearchPage) return;
        const key = JSON.stringify(normalizeCategory(initialFilters.category).sort());
        if (prevInitialCategoryKeyRef.current === key) return;
        prevInitialCategoryKeyRef.current = key;

        if (pageType === 'empresas') {
            setListingFilters((prev) => ({ ...prev, category: normalizeCategory(initialFilters.category) }));
        } else {
            setFilters((prev) => ({ ...prev, category: normalizeCategory(initialFilters.category) }));
        }

        isInitialMount.current = true;
        lastSearchSignatureRef.current = '';
    }, [initialFilters.category, isCategorySearchPage, pageType]);

    // Mesmo cuidado para a localização: ao entrar/sair de uma página geo (ou o servidor ajustar a localização/raio),
    // o estado passa a refletir o que veio do servidor, sem disparar uma navegação de volta.
    useEffect(() => {
        if (prevLocationPropsKeyRef.current === locationPropsKey) return;
        prevLocationPropsKeyRef.current = locationPropsKey;

        const location = locationFilter?.selected ?? null;
        const radius = locationFilter?.radius ?? null;

        if (pageType === 'empresas') {
            setListingFilters((prev) => ({ ...prev, location, radius }));
        } else {
            setFilters((prev) => ({ ...prev, location, radius }));
        }

        isInitialMount.current = true;
        lastSearchSignatureRef.current = '';
    }, [locationFilter, locationPropsKey, pageType]);

    const areaServedSchema = useMemo(() => {
        if (!isGeoRoute || !categorySeo?.location) {
            return null;
        }

        const { location } = categorySeo;
        const country = { '@type': 'Country', name: location.country.name };

        if (location.type === 'city' && location.city) {
            return {
                '@type': 'City',
                name: location.city.name,
                containedInPlace: {
                    '@type': 'State',
                    name: location.state.name,
                    containedInPlace: country,
                },
            };
        }

        return {
            '@type': 'State',
            name: location.state.name,
            containedInPlace: country,
        };
    }, [categorySeo?.location, isGeoRoute]);

    // O mainEntity só aponta para o ItemList quando ele existe (há resultados com slug).
    const hasListingStockItems = isListingStockPage && (results?.data || []).some((item) => typeof item?.slug === 'string' && item.slug.length > 0);

    const collectionPageSchema = useMemo(() => {
        if (!isCategorySearchPage || !canonicalUrl) {
            return null;
        }

        return {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            '@id': `${canonicalUrl}#collection`,
            name: categorySchemaName || categoryHeadingTitle,
            url: canonicalUrl,
            description: categoryMetaDescription,
            inLanguage: 'pt-BR',
            ...(areaServedSchema ? { areaServed: areaServedSchema } : {}),
            ...(isListingStockPage && categorySeo?.about ? { about: categorySeo.about } : {}),
            ...(isListingStockPage && hasListingStockItems ? { mainEntity: { '@id': `${canonicalUrl}#itemlist` } } : {}),
        };
    }, [
        areaServedSchema,
        canonicalUrl,
        categoryHeadingTitle,
        categoryMetaDescription,
        categorySchemaName,
        categorySeo?.about,
        hasListingStockItems,
        isCategorySearchPage,
        isListingStockPage,
    ]);

    const breadcrumbSchema = useMemo(() => {
        if (!isCategorySearchPage || !canonicalUrl) {
            return null;
        }

        const items = [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Início',
                item: toAbsoluteUrl(appUrl, '/'),
            },
            ...categoryBreadcrumbs.map((breadcrumb, index) => ({
                '@type': 'ListItem',
                position: index + 2,
                name: breadcrumb.name,
                item: toAbsoluteUrl(appUrl, breadcrumb.path),
            })),
        ];

        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: items,
        };
    }, [appUrl, canonicalUrl, categoryBreadcrumbs, isCategorySearchPage]);

    const itemListSchema = useMemo(() => {
        const categoryResults = (results?.data || []).filter((item) => typeof item?.slug === 'string' && item.slug.length > 0);

        if (!isCategorySearchPage || categoryResults.length === 0) {
            return null;
        }

        const startPosition = results?.from ?? 1;

        return {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            ...(isListingStockPage ? { '@id': `${canonicalUrl}#itemlist` } : {}),
            itemListOrder: 'https://schema.org/ItemListOrderDescending',
            numberOfItems: results?.total ?? categoryResults.length,
            // Só nome e URL: preço/imagem transformariam cada item em Product/Offer (exigências do Google).
            itemListElement: categoryResults.map((item, index) => ({
                '@type': 'ListItem',
                position: startPosition + index,
                ...(normalizeText(item.title) ? { name: normalizeText(item.title) } : {}),
                url: toAbsoluteUrl(appUrl, pageType === 'empresas' ? showListing({ path: item.slug }).url : buildClassifiedPath(item.slug)),
            })),
        };
    }, [appUrl, canonicalUrl, isCategorySearchPage, isListingStockPage, pageType, results?.data, results?.from, results?.total]);

    const faqSchema = useMemo(() => {
        if (!isCategorySearchPage || faqItems.length === 0) {
            return null;
        }

        return {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.answer,
                },
            })),
        };
    }, [faqItems, isCategorySearchPage]);

    const sanitizedIntroHtml = useMemo(() => sanitizeHtmlContent(categoryIntroText), [categoryIntroText]);
    const sanitizedLongDescriptionHtml = useMemo(() => sanitizeHtmlContent(longDescription), [longDescription]);
    const hasPostListingContent =
        Boolean(normalizeText(categorySeo?.seo_h2)) ||
        Boolean(resumoRapido) ||
        Boolean(longDescription) ||
        pontosDestaque.length > 0 ||
        faqItems.length > 0 ||
        relatedTags.length > 0 ||
        synonyms.length > 0;

    const listings = useMemo(() => {
        if (pageType !== 'empresas') {
            return [];
        }

        return (results?.data || []) as Listing[];
    }, [pageType, results?.data]);

    const classifieds = useMemo(() => {
        if (pageType !== 'embarcacoes') {
            return [];
        }

        return (results?.data || []) as Classified[];
    }, [pageType, results?.data]);

    const boatCards = useMemo(() => toBoatCards(classifieds, favoriteStates), [classifieds, favoriteStates]);
    const listingCards = useMemo(() => toListingCards(listings, favoriteStates), [listings, favoriteStates]);

    const buildSearchParams = useCallback(
        (searchTerm = searchTermOverride ?? initialFilters.search): ResultsSearchParams => {
            const searchParams: ResultsSearchParams = {
                sort: sortBy,
                type: pageType,
            };

            if (searchTerm?.trim()) {
                searchParams.search = searchTerm.trim();
            }

            const activeFilters = pageType === 'empresas' ? listingFilters : filters;

            // Na página geo (que mantém a URL só com a categoria da página) a categoria já está no caminho.
            const secondaryFilterKeys = (staysOnGeoRoute ? [] : activeCategoryIds)
                .map((id) => resolveCategoryFilterKey(id))
                .filter((filterKey): filterKey is string => Boolean(filterKey));

            const shouldIncludeCategoryParam = isListingStockPage || resultsRouteUrl === search().url;

            if (shouldIncludeCategoryParam) {
                if (secondaryFilterKeys.length === 1) {
                    searchParams.category = secondaryFilterKeys[0];
                } else if (secondaryFilterKeys.length > 1) {
                    searchParams.category = secondaryFilterKeys;
                }
            }

            // Uma localização por vez: estado pelo slug, cidade pelo id + raio. Na página geo, a localização é a do caminho.
            if (activeFilters.location && !staysOnGeoRoute) {
                if (activeFilters.location.type === 'state') {
                    searchParams.state = activeFilters.location.slug || String(activeFilters.location.id);
                } else {
                    searchParams.city = activeFilters.location.id;

                    if (activeFilters.radius !== null) {
                        searchParams.radius = activeFilters.radius;
                    }
                }
            }

            if (pageType === 'embarcacoes') {
                if (filters.condition !== 'all') {
                    searchParams.condition = filters.condition;
                }

                if (filters.yearRange[0] !== yearMin) {
                    searchParams.year_min = filters.yearRange[0];
                }
                if (filters.yearRange[1] !== yearMax) {
                    searchParams.year_max = filters.yearRange[1];
                }

                if (filters.priceRange[0] !== priceMin) {
                    searchParams.price_min = filters.priceRange[0];
                }
                if (filters.priceRange[1] !== priceMax) {
                    searchParams.price_max = filters.priceRange[1];
                }

                if (filters.hours[0] > 0) {
                    searchParams.sailing_time_min = filters.hours[0];
                }
                if (filters.hours[1] < DEFAULT_SAILING_MAX) {
                    searchParams.sailing_time_max = filters.hours[1];
                }

                // CN-200 (item 7): size, engine and comfort filters, all in the URL.
                if (filters.sizeMin !== null) searchParams.size_min = filters.sizeMin;
                if (filters.sizeMax !== null) searchParams.size_max = filters.sizeMax;
                if (filters.fuelType) searchParams.fuel_type = filters.fuelType;
                if (filters.engineBrand) searchParams.engine_brand = filters.engineBrand;
                if (filters.cabin) searchParams.cabin = 1;
                if (filters.bathroom) searchParams.bathroom = 1;
                if (filters.generator) searchParams.generator = 1;
                if (filters.passengersMin) searchParams.passengers_min = filters.passengersMin;
            } else {
                // CN-200 (item 7): company filters.
                if (listingFilters.service.trim()) searchParams.service = listingFilters.service.trim();
                if (listingFilters.openSaturday) searchParams.open_saturday = 1;
                if (listingFilters.openSunday) searchParams.open_sunday = 1;
                if (listingFilters.openNow) searchParams.open_now = 1;
                if (listingFilters.ratingMin !== null) searchParams.rating_min = listingFilters.ratingMin;
                if (listingFilters.verified) searchParams.verified = 1;
            }

            // The smart-search phrase stays in the URL (and names the alert) until the visitor changes a filter by hand.
            if (smartSearchPhrase && !hasManualChange) {
                searchParams[SMART_QUERY_PARAM] = smartSearchPhrase;

                if (smartSearchMissed) {
                    searchParams[SMART_MISS_PARAM] = 1;
                }
            }

            return searchParams;
        },
        [
            resolveCategoryFilterKey,
            activeCategoryIds,
            filters,
            hasManualChange,
            initialFilters.search,
            searchTermOverride,
            isListingStockPage,
            listingFilters,
            pageType,
            priceMax,
            priceMin,
            resultsRouteUrl,
            smartSearchMissed,
            smartSearchPhrase,
            sortBy,
            staysOnGeoRoute,
            yearMax,
            yearMin,
        ],
    );

    useEffect(() => {
        const searchParams = buildSearchParams();
        const nextSignature = JSON.stringify({ url: resultsRouteUrl, params: searchParams });

        if (isInitialMount.current) {
            isInitialMount.current = false;
            lastSearchSignatureRef.current = nextSignature;
            return;
        }

        if (nextSignature === lastSearchSignatureRef.current) {
            return;
        }

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = window.setTimeout(() => {
            lastSearchSignatureRef.current = nextSignature;

            router.get(resultsRouteUrl, searchParams as InertiaPayload, {
                preserveState: true,
                preserveScroll: true,
                replace: true,
            });
        }, 300);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [buildSearchParams, resultsRouteUrl]);

    useEffect(() => {
        setIsSeoLongContentOpen(false);
        setIsIntroExpanded(false);
        setOpenFaqIndexes(new Set());
    }, [categorySeo?.id]);

    // O corte do intro é só visual (line-clamp no mobile); mede se o texto realmente passa de 2 linhas para exibir o "Ler mais".
    useEffect(() => {
        const element = introTextRef.current;

        if (!element || isIntroExpanded) {
            return;
        }

        const measure = () => setIsIntroClamped(element.scrollHeight > element.clientHeight + 1);
        measure();

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(measure);
        observer.observe(element);

        return () => observer.disconnect();
    }, [isIntroExpanded, sanitizedIntroHtml]);

    const favoriteParamKey = pageType === 'empresas' ? 'favorite_listing_id' : 'favorite_id';

    const clearAutoFavoriteParams = useCallback(
        (url: URL) => {
            url.searchParams.delete('auto_favorite');
            url.searchParams.delete(favoriteParamKey);
            window.history.replaceState(null, '', url.toString());
        },
        [favoriteParamKey],
    );

    const favoriteEndpoint = useCallback(
        (resultId: number) => {
            return pageType === 'empresas' ? `/api/favorites/listing/${resultId}/toggle` : `/api/favorites/${resultId}/toggle`;
        },
        [pageType],
    );

    const redirectToLoginForFavorite = useCallback(
        (resultId: number) => {
            if (typeof window === 'undefined') {
                return;
            }

            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set('auto_favorite', '1');
            currentUrl.searchParams.set(favoriteParamKey, String(resultId));

            router.visit(login().url, {
                data: { return_url: currentUrl.toString() },
            });
        },
        [favoriteParamKey],
    );

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        if (!auth?.user) {
            return;
        }

        try {
            const url = new URL(window.location.href);
            const autoFavorite = url.searchParams.get('auto_favorite');
            const favoriteIdParam = url.searchParams.get(favoriteParamKey);

            if (autoFavorite !== '1' || !favoriteIdParam) {
                return;
            }

            const signature = `${favoriteParamKey}:${favoriteIdParam}`;
            if (handledAutoFavoriteRef.current === signature) {
                return;
            }

            const favoriteId = Number(favoriteIdParam);
            if (!favoriteId || Number.isNaN(favoriteId)) {
                clearAutoFavoriteParams(url);
                return;
            }

            const resultItems = (results?.data || []) as Array<{ id: number; is_favorited?: boolean }>;
            const targetItem = resultItems.find((item) => item.id === favoriteId);

            if (!targetItem) {
                clearAutoFavoriteParams(url);
                return;
            }

            const alreadyFavorited = favoriteStates[favoriteId] ?? targetItem.is_favorited ?? false;
            if (alreadyFavorited) {
                handledAutoFavoriteRef.current = signature;
                clearAutoFavoriteParams(url);
                return;
            }

            handledAutoFavoriteRef.current = signature;

            apiClient
                .post<{ is_favorited: boolean }>(favoriteEndpoint(favoriteId))
                .then((response) => {
                    setFavoriteStates((previous) => ({
                        ...previous,
                        [favoriteId]: response.data.is_favorited,
                    }));
                })
                .catch((error) => {
                    console.error('Error auto-toggling favorite after login on results page:', error);
                })
                .finally(() => {
                    clearAutoFavoriteParams(url);
                });
        } catch (error) {
            console.error('Error handling auto_favorite params on results page:', error);
        }
    }, [auth?.user, clearAutoFavoriteParams, favoriteEndpoint, favoriteParamKey, favoriteStates, results?.data]);

    const handleFiltersChange = (newFilters: BoatFilters) => {
        setHasManualChange(true);
        setFilters(newFilters);
    };

    const handleListingFiltersChange = (newFilters: ListingFilters) => {
        setHasManualChange(true);
        setListingFilters(newFilters);
    };

    // A new smart-search navigation starts again with "Entendi assim:".
    useEffect(() => {
        setHasManualChange(false);
    }, [smartSearchPhrase]);

    // ?filters=1 (home "Prefiro escolher nos filtros" / mobile search): opens the drawer once and drops the flag.
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const url = new URL(window.location.href);

        if (url.searchParams.get('filters') !== '1') {
            return;
        }

        url.searchParams.delete('filters');
        window.history.replaceState(window.history.state, '', url.toString());
        setIsFiltersDrawerOpen(true);
    }, []);

    // "Ver N embarcações / empresas" of the drawer: counted on the server with the same rules of the search.
    useEffect(() => {
        if (!isFiltersDrawerOpen) {
            return;
        }

        const requestId = ++drawerCountRequestRef.current;
        setDrawerCount((previous) => ({ status: 'loading', count: previous.count }));

        const timeout = window.setTimeout(() => {
            const params = { ...buildSearchParams() } as Record<string, unknown>;
            delete params[SMART_QUERY_PARAM];
            delete params[SMART_MISS_PARAM];
            delete params.sort;

            if (!params.type) {
                params.type = pageType;
            }

            // On a category / geo page the category and the location live in the path, so buildSearchParams leaves them
            // out: the count needs them to match the page.
            if (params.category === undefined && !isListingStockPage) {
                const categoryKeys = activeCategoryIds
                    .map((id) => resolveCategoryFilterKey(id))
                    .filter((filterKey): filterKey is string => Boolean(filterKey));

                if (categoryKeys.length === 1) {
                    params.category = categoryKeys[0];
                } else if (categoryKeys.length > 1) {
                    params.category = categoryKeys;
                }
            }

            if (staysOnGeoRoute && activeLocation) {
                if (activeLocation.type === 'state') {
                    params.state = activeLocation.slug || String(activeLocation.id);
                } else {
                    // City geo page = "Somente na cidade".
                    params.city = activeLocation.id;
                    params.radius = 0;
                }
            }

            apiClient
                .get<{ count: number }>(searchCountRoute.url(), { params })
                .then((response) => {
                    if (drawerCountRequestRef.current === requestId) {
                        setDrawerCount({ status: 'ready', count: response.data.count });
                    }
                })
                .catch((error: unknown) => {
                    if (drawerCountRequestRef.current === requestId) {
                        console.error('Error counting the results of the filters drawer:', error);
                        setDrawerCount({ status: 'error', count: null });
                    }
                });
        }, 250);

        return () => window.clearTimeout(timeout);
    }, [
        activeCategoryIds,
        activeLocation,
        buildSearchParams,
        isFiltersDrawerOpen,
        isListingStockPage,
        pageType,
        resolveCategoryFilterKey,
        staysOnGeoRoute,
    ]);

    const radiusOptions = locationFilter?.radius_options ?? [];
    const defaultRadius = locationFilter?.default_radius ?? 0;

    // Sem resultado com raio menor que o maior da lista: sugere o próximo raio (usa o raio que o servidor aplicou).
    const appliedRadius = locationFilter?.radius ?? null;
    const nextRadiusSuggestion =
        (results?.total ?? 0) === 0 && locationSupportsRadius(locationFilter?.selected ?? null) && appliedRadius !== null
            ? (radiusOptions.find((option) => option > appliedRadius) ?? null)
            : null;
    const emptyResultsNoun = pageType === 'empresas' ? 'Nenhuma empresa' : 'Nenhuma embarcação';
    const emptyRadiusMessage =
        appliedRadius === 0
            ? `${emptyResultsNoun} em ${locationFilter?.selected?.label ?? 'nesta cidade'}.`
            : `${emptyResultsNoun} até ${appliedRadius} km.`;

    const handleExpandRadius = (nextRadius: number) => {
        if (pageType === 'empresas') {
            setListingFilters((previous) => ({ ...previous, radius: nextRadius }));
        } else {
            setFilters((previous) => ({ ...previous, radius: nextRadius }));
        }
    };

    const handleSortChange = (newSort: SortOption) => {
        setHasManualChange(true);
        setSortBy(newSort);
    };

    // CN-200: removing a label is a navigation decided by the server (same search without it); the page remounts with fresh filters.
    const handleRemoveCriteriaLabel = useCallback(
        (label: SearchCriteriaLabel) => {
            trackSearchEvent('busca_etiqueta_removida', pageType, { etiqueta: label.id.split(':')[0] });
            router.visit(label.remove_url, { preserveState: false, preserveScroll: true });
        },
        [pageType],
    );

    const handleZeroResultSuggestion = useCallback(() => {
        if (!zeroResultSuggestion) {
            return;
        }

        trackSearchEvent('busca_sugestao_zero_click', pageType, { etiqueta: zeroResultSuggestion.label_id.split(':')[0] });
        router.visit(zeroResultSuggestion.url, { preserveState: false, preserveScroll: true });
    }, [pageType, zeroResultSuggestion]);

    const handleFaqToggle = (faqIndex: number, isOpen: boolean) => {
        setOpenFaqIndexes((previous) => {
            const updated = new Set(previous);

            if (isOpen) {
                updated.add(faqIndex);
            } else {
                updated.delete(faqIndex);
            }

            return updated;
        });
    };

    const handleSearchSubmit = useCallback(
        (query: string) => {
            const searchParams = buildSearchParams(query);
            lastSearchSignatureRef.current = JSON.stringify(searchParams);

            router.get(resultsRouteUrl, searchParams as InertiaPayload, {
                preserveState: false,
                preserveScroll: false,
            });
        },
        [buildSearchParams, resultsRouteUrl],
    );

    const handleSearchClear = useCallback(() => {
        if (!initialFilters.search?.trim()) {
            return;
        }

        handleSearchSubmit('');
    }, [handleSearchSubmit, initialFilters.search]);

    // A busca por texto da URL mudou (nova navegação): volta a valer o que veio do servidor.
    useEffect(() => {
        setSearchTermOverride(null);
    }, [initialFilters.search]);

    const handleSearchLocationSelect = (location: SelectedLocation) => {
        const applyLocation = <T extends { location: SelectedLocation | null; radius: number | null }>(previous: T): T => ({
            ...previous,
            location,
            radius: resolveNextRadius(previous.location, previous.radius, location, defaultRadius),
        });

        setSearchTermOverride('');

        if (pageType === 'empresas') {
            setListingFilters(applyLocation);
        } else {
            setFilters(applyLocation);
        }
    };

    // Critérios do alerta (CN-200 item 10.2): o pacote montado no servidor para a busca atual; sem ele (páginas que ainda
    // não o enviam), os filtros da lateral (mesma regra da URL: limite do slider = sem filtro).
    const alertCriteria = useMemo(() => {
        if (searchCriteria && !hasManualChange) {
            return buildAlertCriteriaFromSearch(searchCriteria, { useOriginalText: Boolean(smartSearchPhrase) });
        }

        return buildSearchAlertCriteria(filters, {
            filterOptions,
            bounds: { yearMin, yearMax, priceMin, priceMax, hoursMin: 0, hoursMax: DEFAULT_SAILING_MAX },
            resolveCategoryName: (categoryId) => (categorySeo?.id === categoryId ? normalizeText(categorySeo?.name) || undefined : undefined),
        });
    }, [categorySeo?.id, categorySeo?.name, filterOptions, filters, hasManualChange, priceMax, priceMin, searchCriteria, smartSearchPhrase, yearMax, yearMin]);

    // Estoque de uma empresa: o alerta não teria o recorte da empresa, então o botão não aparece lá. Empresas não têm alerta.
    const canCreateAlert = pageType === 'embarcacoes' && !isListingStockPage;
    const hasResults = (results?.total ?? 0) > 0;
    const defaultAlertSource: SearchAlertSource = !hasResults ? 'busca_sem_resultado' : smartSearchPhrase && !hasManualChange ? 'busca_ia' : 'busca';
    const smartSearchEnabled = smartSearchSettings.enabled[pageType] ?? false;
    const criteriaLabels = searchCriteria?.labels ?? [];
    const criteriaTitle = criteriaLabels.length === 0 ? null : smartSearchPhrase && !hasManualChange && !smartSearchMissed ? 'Entendi assim:' : 'Sua busca:';
    const activeFilterCount =
        pageType === 'empresas'
            ? listingFilters.category.length + (listingFilters.location ? 1 : 0) + countListingAttributeFilters(listingFilters)
            : filters.category.length +
              (filters.condition !== 'all' ? 1 : 0) +
              (filters.location ? 1 : 0) +
              (filters.yearRange[0] !== yearMin || filters.yearRange[1] !== yearMax ? 1 : 0) +
              (filters.priceRange[0] !== priceMin || filters.priceRange[1] !== priceMax ? 1 : 0) +
              (filters.hours[0] > 0 || filters.hours[1] < DEFAULT_SAILING_MAX ? 1 : 0) +
              countBoatAttributeFilters(filters);
    const resultNoun = pageType === 'empresas' ? ['empresa', 'empresas'] : ['embarcação', 'embarcações'];

    const openFiltersDrawer = useCallback(() => {
        trackSearchEvent('busca_filtros_abertos', pageType, { origem: 'resultados' });
        setIsFiltersDrawerOpen(true);
    }, [pageType]);

    const closeFiltersDrawer = useCallback(() => {
        trackSearchEvent('busca_ver_resultados_click', pageType, { total: drawerCount.count ?? results?.total ?? 0 });
        setIsFiltersDrawerOpen(false);
    }, [drawerCount.count, pageType, results?.total]);

    const clearAllFilters = useCallback(() => {
        setHasManualChange(true);

        if (pageType === 'empresas') {
            setListingFilters({ category: [], location: null, radius: null, ...EMPTY_LISTING_ATTRIBUTE_FILTERS });
        } else {
            setFilters({
                category: [],
                condition: 'all',
                location: null,
                radius: null,
                yearRange: [yearMin, yearMax],
                priceRange: [priceMin, priceMax],
                hours: [0, DEFAULT_SAILING_MAX],
                ...EMPTY_BOAT_ATTRIBUTE_FILTERS,
            });
        }
    }, [pageType, priceMax, priceMin, yearMax, yearMin]);

    const handleCreateAlert = useCallback(
        (source: SearchAlertSource) => {
            if (!auth?.user) {
                // Visitante: guarda a intenção e volta para esta mesma busca com o modal aberto após o login.
                saveCreateAlertDraft({ source, created_at: new Date().toISOString() });

                router.visit(login().url, {
                    data: { return_url: buildCreateAlertReturnUrl() },
                });
                return;
            }

            setCreateAlertDraft(null);
            setCreateAlertSource(source);
            setIsCreateAlertOpen(true);
        },
        [auth?.user],
    );

    // Volta do login (?criar_alerta=1): reabre o modal com o rascunho e limpa o parâmetro da URL.
    useEffect(() => {
        if (typeof window === 'undefined' || !auth?.user || !canCreateAlert || handledCreateAlertReturnRef.current) {
            return;
        }

        const url = new URL(window.location.href);

        if (url.searchParams.get(CREATE_ALERT_QUERY_PARAM) !== '1') {
            return;
        }

        handledCreateAlertReturnRef.current = true;

        const draft = readCreateAlertDraft();
        clearCreateAlertDraft();
        clearCreateAlertQueryParam();

        setCreateAlertDraft(draft);
        setCreateAlertSource(draft?.source ?? 'busca');
        setIsCreateAlertOpen(true);
    }, [auth?.user, canCreateAlert]);

    const handleFavorite = async (resultId: number) => {
        if (!auth?.user) {
            redirectToLoginForFavorite(resultId);
            return;
        }

        try {
            const response = await apiClient.post<{ is_favorited: boolean }>(favoriteEndpoint(resultId));

            setFavoriteStates((previous) => ({
                ...previous,
                [resultId]: response.data.is_favorited,
            }));
        } catch (error: unknown) {
            if (hasHttpStatus(error, 401)) {
                redirectToLoginForFavorite(resultId);
                return;
            }

            console.error('Error toggling favorite:', error);
        }
    };

    return (
        <FrontendLayout>
            <Head title={pageTitle}>
                {isCategorySearchPage && categoryMetaDescription && <meta name="description" content={categoryMetaDescription} />}
                {isCategorySearchPage && canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
                {isCategorySearchPage && <meta name="robots" content="index,follow" />}

                {isCategorySearchPage && <meta property="og:title" content={pageTitle} />}
                {isCategorySearchPage && categoryMetaDescription && <meta property="og:description" content={categoryMetaDescription} />}
                {isCategorySearchPage && canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
                {isCategorySearchPage && <meta property="og:type" content="website" />}
                {isCategorySearchPage && <meta property="og:locale" content="pt_BR" />}
                {isCategorySearchPage && categorySocialImage && <meta property="og:image" content={categorySocialImage} />}

                {collectionPageSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: toJsonLd(collectionPageSchema) }} />}
                {breadcrumbSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: toJsonLd(breadcrumbSchema) }} />}
                {itemListSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: toJsonLd(itemListSchema) }} />}
                {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: toJsonLd(faqSchema) }} />}

                <link rel="preconnect" href="https://fonts.bunny.net" />
                <link href="https://fonts.bunny.net/css?family=instrument-sans:400,500,600" rel="stylesheet" />
            </Head>

            <div className="w-full bg-gray-100 pb-24">
                <div className="container mx-auto px-4 py-8">
                    {isCategorySearchPage && (
                        <section className="mb-6 rounded-xl bg-white p-4 sm:p-6">
                            <nav aria-label="Breadcrumb" className="mb-2">
                                <ol className="flex flex-wrap items-center gap-2 text-xs text-gray-500 sm:text-sm">
                                    <li>
                                        <a href="/" className="transition-colors hover:text-gray-700">
                                            Início
                                        </a>
                                    </li>

                                    {categoryBreadcrumbs.map((breadcrumb, index) => {
                                        const isLast = index === categoryBreadcrumbs.length - 1;
                                        return (
                                            <li key={`${breadcrumb.path}-${index}`} className="inline-flex items-center gap-2">
                                                <span aria-hidden="true">/</span>
                                                {isLast ? (
                                                    <span className="font-medium text-gray-700">{breadcrumb.name}</span>
                                                ) : (
                                                    <a href={breadcrumb.path} className="transition-colors hover:text-gray-700">
                                                        {breadcrumb.name}
                                                    </a>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ol>
                            </nav>

                            {isListingStockPage && listingProfile && <ListingStockHeader profile={listingProfile} />}

                            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl lg:text-3xl">
                                {categoryHeadingTitle || 'Embarcações à venda'}
                            </h1>

                            {isListingStockPage && normalizeText(categorySeo?.subtitle) && (
                                <p className="mt-1 text-sm text-gray-600 sm:text-base">{normalizeText(categorySeo?.subtitle)}</p>
                            )}

                            {categoryIntroText && (
                                <div className="mt-3">
                                    {/*
                                        Texto inteiro no HTML; no mobile só o corte visual (line-clamp), sem display:none.
                                        Não trocar pelo Collapsible: ele esconde o conteúdo com `hidden` (display:none), e texto
                                        escondido do HTML visível conta como conteúdo oculto para o Google (CN-182, item 5).
                                    */}
                                    <div
                                        ref={introTextRef}
                                        id="category-intro-text"
                                        className={cn(
                                            'prose prose-neutral max-w-none text-sm text-gray-700 sm:line-clamp-none sm:text-base',
                                            !isIntroExpanded && 'line-clamp-2',
                                        )}
                                        dangerouslySetInnerHTML={{ __html: sanitizedIntroHtml }}
                                    />

                                    {/* Sempre no DOM para reservar o espaço (sem layout shift); se o texto cabe em 2 linhas, só fica invisível. */}
                                    <button
                                        type="button"
                                        onClick={() => setIsIntroExpanded((previous) => !previous)}
                                        aria-expanded={isIntroExpanded}
                                        aria-controls="category-intro-text"
                                        className={cn(
                                            'mt-1 inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-gray-900 sm:hidden',
                                            !isIntroClamped && !isIntroExpanded && 'invisible',
                                        )}
                                    >
                                        {isIntroExpanded ? 'Ler menos' : 'Ler mais'}
                                        {isIntroExpanded ? (
                                            <ChevronUp className="h-4 w-4 text-gray-500" />
                                        ) : (
                                            <ChevronDown className="h-4 w-4 text-gray-500" />
                                        )}
                                    </button>
                                </div>
                            )}
                        </section>
                    )}

                    {/* CN-200: barra fixa do celular (frase, Filtros (n), ordenar, Criar alerta) */}
                    <div className="sticky top-0 z-30 -mx-4 mb-4 border-b border-gray-200 bg-white px-4 py-3 shadow-sm lg:hidden">
                        {smartSearchEnabled ? (
                            <SmartSearchForm
                                type={pageType}
                                origin="resultados"
                                initialText={smartSearchPhrase ?? initialFilters.search ?? ''}
                                showExamples={false}
                                size="md"
                                compact
                                onResult={(result) => router.visit(result.url)}
                            />
                        ) : (
                            <SearchAutocomplete
                                placeholder={pageType === 'empresas' ? 'Busque por serviço, empresa ou local' : 'Busque por marca, modelo ou local'}
                                initialValue={initialFilters.search || ''}
                                showButton={false}
                                searchType={pageType}
                                onSearch={handleSearchSubmit}
                                onClear={handleSearchClear}
                                onLocationSelect={handleSearchLocationSelect}
                            />
                        )}

                        <div className="mt-2 flex items-center gap-2">
                            <Button type="button" variant="outline" onClick={openFiltersDrawer} className="h-11 flex-1 justify-center">
                                <SlidersHorizontal aria-hidden="true" className="h-4 w-4" />
                                Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                            </Button>

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button type="button" variant="outline" size="icon" className="h-11 w-11 shrink-0" aria-label="Ordenar resultados">
                                        <ArrowUpDown aria-hidden="true" className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    {sortOptions.map((option) => (
                                        <DropdownMenuItem
                                            key={option.value}
                                            onSelect={() => handleSortChange(option.value)}
                                            className={cn(option.value === sortBy && 'font-semibold text-blue-800')}
                                        >
                                            {option.label}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>

                            {canCreateAlert && (
                                <Button
                                    type="button"
                                    onClick={() => handleCreateAlert(defaultAlertSource)}
                                    className="h-11 flex-1 bg-[#00D26A] text-gray-900 hover:bg-[#00bd5f]"
                                >
                                    <BellRing aria-hidden="true" className="h-4 w-4" />
                                    Criar alerta
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Computador: faixa com a frase + etiquetas + "Criar alerta desta busca" */}
                    <div className="mb-6 hidden lg:block">
                        {smartSearchEnabled ? (
                            <SmartSearchForm
                                type={pageType}
                                origin="resultados"
                                initialText={smartSearchPhrase ?? initialFilters.search ?? ''}
                                showExamples={!smartSearchPhrase && !initialFilters.search}
                                onResult={(result) => router.visit(result.url)}
                            />
                        ) : (
                            <SearchAutocomplete
                                placeholder={pageType === 'empresas' ? 'Busque por serviço, empresa ou local' : 'Busque por marca, modelo ou local'}
                                initialValue={initialFilters.search || ''}
                                showButton={false}
                                searchType={pageType}
                                onSearch={handleSearchSubmit}
                                onClear={handleSearchClear}
                                onLocationSelect={handleSearchLocationSelect}
                            />
                        )}
                    </div>

                    {(criteriaLabels.length > 0 || smartSearchMissed || (canCreateAlert && criteriaLabels.length > 0)) && (
                        <section aria-label="Filtros aplicados" className="mb-6 rounded-xl bg-white p-4 sm:p-5">
                            {smartSearchMissed && (
                                <p role="status" className="mb-3 text-sm text-gray-700">
                                    Não consegui transformar essa frase em filtros. Tente dizer o tipo de {pageType === 'empresas' ? 'serviço' : 'embarcação'}, o
                                    valor e o local, ou toque em Filtros para escolher à mão.
                                </p>
                            )}

                            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                <SearchCriteriaChips labels={criteriaLabels} onRemove={handleRemoveCriteriaLabel} type={pageType} title={criteriaTitle} />

                                {canCreateAlert && criteriaLabels.length > 0 && (
                                    <Button
                                        type="button"
                                        onClick={() => handleCreateAlert(defaultAlertSource)}
                                        className="hidden shrink-0 bg-[#00D26A] text-gray-900 hover:bg-[#00bd5f] lg:inline-flex"
                                    >
                                        <BellRing aria-hidden="true" className="h-4 w-4" />
                                        Criar alerta desta busca
                                    </Button>
                                )}
                            </div>
                        </section>
                    )}

                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr]">
                        <div className="hidden lg:sticky lg:top-8 lg:block lg:h-[calc(100dvh-2rem)] lg:self-start">
                            {pageType === 'embarcacoes' ? (
                                <BoatFilterSidebar
                                    filters={filters}
                                    onFiltersChange={handleFiltersChange}
                                    onCreateAlert={canCreateAlert ? () => handleCreateAlert(defaultAlertSource) : undefined}
                                    filterOptions={filterOptions}
                                    className={boatSidebarClass}
                                    hideLocation={isListingStockPage}
                                    radiusOptions={radiusOptions}
                                    defaultRadius={defaultRadius}
                                />
                            ) : (
                                <ListingFilterSidebar
                                    filters={listingFilters}
                                    onFiltersChange={handleListingFiltersChange}
                                    filterOptions={filterOptions}
                                    className={listingSidebarClass}
                                    radiusOptions={radiusOptions}
                                    defaultRadius={defaultRadius}
                                />
                            )}
                        </div>

                        <div className="space-y-6">
                            <BoatResultsHeader
                                totalResults={results?.total || 0}
                                sortBy={sortBy}
                                onSortChange={handleSortChange}
                                sortOptions={sortOptions}
                                resultLabel={pageType === 'empresas' ? 'empresas encontradas' : 'anúncios encontrados'}
                                heading={resultsHeading}
                            />

                            {zeroResultSuggestion && (results?.total ?? 0) === 0 && (
                                <div role="status" className="flex flex-col gap-3 rounded-xl bg-white p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                                    <p className="text-sm text-gray-700 sm:text-base">
                                        {pageType === 'empresas' ? 'Nenhuma empresa exatamente assim.' : 'Nenhuma embarcação exatamente assim agora.'}
                                    </p>
                                    <Button type="button" onClick={handleZeroResultSuggestion} className="h-11 shrink-0 bg-blue-800 px-6 text-white hover:bg-blue-900 sm:h-10">
                                        Tirar “{zeroResultSuggestion.label_text}” e ver {zeroResultSuggestion.count.toLocaleString('pt-BR')}{' '}
                                        {zeroResultSuggestion.count === 1 ? resultNoun[0] : resultNoun[1]}
                                    </Button>
                                </div>
                            )}

                            {nextRadiusSuggestion !== null && (
                                <div
                                    role="status"
                                    className="flex flex-col gap-3 rounded-xl bg-white p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6"
                                >
                                    <p className="text-sm text-gray-700 sm:text-base">{emptyRadiusMessage}</p>
                                    <Button
                                        type="button"
                                        onClick={() => handleExpandRadius(nextRadiusSuggestion)}
                                        className="h-11 shrink-0 bg-blue-800 px-6 text-white hover:bg-blue-900 sm:h-10"
                                    >
                                        Ver {formatRadiusLabel(nextRadiusSuggestion).toLowerCase()}
                                    </Button>
                                </div>
                            )}

                            {pageType === 'embarcacoes' ? (
                                <BoatResultsGrid
                                    boats={boatCards}
                                    onFavorite={handleFavorite}
                                    type="embarcacoes"
                                    classifiedBanners={classifiedBanners}
                                    onCreateAlert={canCreateAlert ? () => handleCreateAlert('busca_sem_resultado') : undefined}
                                />
                            ) : (
                                <ListingResultsGrid listings={listingCards} onFavorite={handleFavorite} />
                            )}

                            {results && <Pagination data={results} preserveState={false} className="mt-8" />}

                            {canCreateAlert && hasResults && (
                                <div className="flex flex-col items-center gap-3 rounded-xl bg-white p-5 text-center sm:flex-row sm:justify-between sm:text-left">
                                    <p className="text-sm text-gray-700 sm:text-base">Não achou a embarcação certa? Avisamos quando aparecer uma nova assim.</p>
                                    <Button
                                        type="button"
                                        onClick={() => handleCreateAlert(defaultAlertSource)}
                                        className="w-full shrink-0 bg-[#00D26A] text-gray-900 hover:bg-[#00bd5f] sm:w-auto"
                                    >
                                        <BellRing aria-hidden="true" className="h-4 w-4" />
                                        Me avise quando aparecer uma assim
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* CN-200: gaveta de filtros do celular (~90% da tela) com "Ver N" contado no servidor */}
                    <Drawer open={isFiltersDrawerOpen} onOpenChange={setIsFiltersDrawerOpen} shouldScaleBackground={false}>
                        <DrawerContent className="h-[90dvh] lg:hidden" aria-describedby={undefined}>
                            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2">
                                <Button type="button" variant="ghost" size="icon" aria-label="Fechar filtros" onClick={() => setIsFiltersDrawerOpen(false)}>
                                    <X aria-hidden="true" className="h-5 w-5" />
                                </Button>
                                <DrawerTitle className="text-base font-semibold text-gray-900">Filtros</DrawerTitle>
                                <Button type="button" variant="ghost" onClick={clearAllFilters} disabled={activeFilterCount === 0} className="text-sm">
                                    Limpar
                                </Button>
                            </div>
                            <DrawerDescription className="sr-only">Escolha os filtros da busca</DrawerDescription>

                            {smartSearchPhrase && !hasManualChange && !smartSearchMissed && (
                                <p className="border-b border-blue-100 bg-blue-50 px-4 py-2 text-sm text-blue-900">
                                    Já marcamos o que entendemos da sua frase. Mude o que quiser.
                                </p>
                            )}

                            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-3">
                                {pageType === 'embarcacoes' ? (
                                    <BoatFilterSidebar
                                        filters={filters}
                                        onFiltersChange={handleFiltersChange}
                                        filterOptions={filterOptions}
                                        hideLocation={isListingStockPage}
                                        radiusOptions={radiusOptions}
                                        defaultRadius={defaultRadius}
                                        alwaysExpanded
                                        initiallyOpenSections={searchCriteria?.labels.map((label) => label.id.split(':')[0]) ?? []}
                                    />
                                ) : (
                                    <ListingFilterSidebar
                                        filters={listingFilters}
                                        onFiltersChange={handleListingFiltersChange}
                                        filterOptions={filterOptions}
                                        radiusOptions={radiusOptions}
                                        defaultRadius={defaultRadius}
                                        alwaysExpanded
                                    />
                                )}
                            </div>

                            <div className="border-t border-gray-200 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
                                <Button
                                    type="button"
                                    onClick={closeFiltersDrawer}
                                    disabled={drawerCount.status === 'ready' && drawerCount.count === 0}
                                    aria-live="polite"
                                    className="h-12 w-full bg-[#00D26A] text-base font-semibold text-gray-900 hover:bg-[#00bd5f] disabled:bg-gray-200 disabled:text-gray-500"
                                >
                                    {drawerCount.status === 'ready' && drawerCount.count === 0
                                        ? `Nenhuma ${resultNoun[0]}, ajuste os filtros`
                                        : drawerCount.count === null
                                          ? `Ver ${resultNoun[1]}`
                                          : `Ver ${drawerCount.count.toLocaleString('pt-BR')} ${drawerCount.count === 1 ? resultNoun[0] : resultNoun[1]}`}
                                </Button>
                            </div>
                        </DrawerContent>
                    </Drawer>

                    {isListingStockPage && listingProfile && <ListingStockAbout profile={listingProfile} />}

                    {isCategorySearchPage && hasPostListingContent && (
                        <section className="mt-8 space-y-5 rounded-xl bg-white p-4 sm:p-6">
                            <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">{postListingTitle}</h2>

                            {resumoRapido && <p className="text-sm leading-relaxed text-gray-700 sm:text-base">{resumoRapido}</p>}

                            {pontosDestaque.length > 0 && (
                                <ul className="list-disc space-y-2 pl-5 text-sm text-gray-700 sm:text-base">
                                    {pontosDestaque.map((item, index) => (
                                        <li key={`${item}-${index}`}>{item}</li>
                                    ))}
                                </ul>
                            )}

                            {longDescription && (
                                <div className="rounded-lg border border-gray-200 p-4">
                                    <Collapsible open={isSeoLongContentOpen} onOpenChange={setIsSeoLongContentOpen}>
                                        <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 text-left">
                                            <span className="text-sm font-medium text-gray-900 sm:text-base">
                                                {isSeoLongContentOpen
                                                    ? `Ocultar conteúdo sobre ${categorySchemaName || categoryHeadingTitle || 'a categoria'}`
                                                    : `Ler mais sobre ${categorySchemaName || categoryHeadingTitle || 'a categoria'}`}
                                            </span>
                                            {isSeoLongContentOpen ? (
                                                <ChevronUp className="h-4 w-4 text-gray-500" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4 text-gray-500" />
                                            )}
                                        </CollapsibleTrigger>

                                        <CollapsibleContent forceMount className={isSeoLongContentOpen ? 'pt-4' : 'hidden pt-4'}>
                                            <div
                                                className="prose prose-neutral max-w-none text-sm text-gray-700 sm:text-base"
                                                dangerouslySetInnerHTML={{ __html: sanitizedLongDescriptionHtml }}
                                            />
                                        </CollapsibleContent>
                                    </Collapsible>
                                </div>
                            )}

                            {faqItems.length > 0 && (
                                <div className="space-y-3">
                                    <h3 className="text-base font-semibold text-gray-900 sm:text-lg">Perguntas frequentes</h3>

                                    {faqItems.map((faq, index) => {
                                        const isOpen = openFaqIndexes.has(index);

                                        return (
                                            <Collapsible
                                                key={`${faq.question}-${index}`}
                                                open={isOpen}
                                                onOpenChange={(open: boolean) => handleFaqToggle(index, open)}
                                            >
                                                <div className="rounded-lg border border-gray-200">
                                                    <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 p-4 text-left">
                                                        <span className="text-sm font-medium text-gray-900 sm:text-base">{faq.question}</span>
                                                        {isOpen ? (
                                                            <ChevronUp className="h-4 w-4 text-gray-500" />
                                                        ) : (
                                                            <ChevronDown className="h-4 w-4 text-gray-500" />
                                                        )}
                                                    </CollapsibleTrigger>

                                                    <CollapsibleContent forceMount className={isOpen ? 'px-4 pt-0 pb-4' : 'hidden px-4 pt-0 pb-4'}>
                                                        <p className="text-sm leading-relaxed text-gray-700 sm:text-base">{faq.answer}</p>
                                                    </CollapsibleContent>
                                                </div>
                                            </Collapsible>
                                        );
                                    })}
                                </div>
                            )}

                            {(relatedTags.length > 0 || synonyms.length > 0) && (
                                <div className="space-y-4">
                                    {relatedTags.length > 0 && (
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900 sm:text-base">Tags relacionadas</h3>
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                {relatedTags.map((tag, index) => (
                                                    <a
                                                        key={`${tag}-${index}`}
                                                        href={`${search().url}?type=embarcacoes&search=${encodeURIComponent(tag)}`}
                                                        className="rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-100"
                                                    >
                                                        {tag}
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {synonyms.length > 0 && (
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900 sm:text-base">Termos similares</h3>
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                {synonyms.map((term, index) => (
                                                    <a
                                                        key={`${term}-${index}`}
                                                        href={`${search().url}?type=embarcacoes&search=${encodeURIComponent(term)}`}
                                                        className="rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-100"
                                                    >
                                                        {term}
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>
                    )}

                    {canCreateAlert && (
                        <CreateAlertDialog
                            open={isCreateAlertOpen}
                            onOpenChange={setIsCreateAlertOpen}
                            criteria={alertCriteria}
                            source={createAlertSource}
                            initialDraft={createAlertDraft}
                        />
                    )}
                </div>
            </div>
        </FrontendLayout>
    );
}
